import React, { useState, useEffect, useMemo, useCallback } from 'react'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js'
import { Bar } from 'react-chartjs-2'
import {
  Box,
  Typography,
  useTheme,
} from '@mui/material'
import supabase from '../supabaseClient'
import dayjs from 'dayjs'
import TimePeriodFilter from './ui/TimePeriodFilter'

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
)

const ClassStudentAttendanceChart = ({ classId, teacherId, isAdmin }) => {
  const [studentAttendanceData, setStudentAttendanceData] = useState([])
  const [loading, setLoading] = useState(true)
  const [viewMode, setViewMode] = useState('month')
  const theme = useTheme()

  // Handle view mode change
  const handleViewModeChange = useCallback((event, newValue) => {
    if (newValue !== null) {
      setViewMode(newValue)
    }
  }, [])

  // Fetch attendance data by student
  useEffect(() => {
    const fetchStudentAttendanceData = async () => {
      if (!classId) return

      try {
        setLoading(true)

        // Get current date
        const now = dayjs()
        let startDate

        // Calculate start date based on view mode
        switch (viewMode) {
          case 'week':
            startDate = now.subtract(7, 'day').format('YYYY-MM-DD')
            break
          case 'month':
            startDate = now.subtract(30, 'day').format('YYYY-MM-DD')
            break
          case '6months':
            startDate = now.subtract(180, 'day').format('YYYY-MM-DD')
            break
          case 'year':
            startDate = now.subtract(365, 'day').format('YYYY-MM-DD')
            break
          default:
            startDate = now.subtract(30, 'day').format('YYYY-MM-DD')
        }

        let query = supabase
          .from('attendance')
          .select(
            `
            status, 
            date,
            student_id,
            students(first_name, last_name)
          `,
          )
          .gte('date', startDate)

        if (classId === 'all') {
          if (isAdmin) {
            // For admin, don't filter by class_id to get all classes
          } else if (teacherId) {
            // For teachers, get all their classes
            const { data: classesData, error: classesError } = await supabase
              .from('classes')
              .select('id')
              .eq('teacher_id', teacherId)

            if (classesError) {
              console.error('Error fetching teacher classes:', classesError)
              return
            }

            if (classesData && classesData.length > 0) {
              const classIds = classesData.map((cls) => cls.id)
              query = query.in('class_id', classIds)
            }
          }
        } else {
          // For a specific class
          query = query.eq('class_id', classId)
        }

        const { data, error } = await query

        if (error) {
          console.error('Error fetching student attendance data:', error)
          return
        }

        // Group attendance data by student
        const studentData = {}
        data.forEach((record) => {
          const studentId = record.student_id
          const studentName = record.students
            ? `${record.students.first_name} ${record.students.last_name}`
            : `Student ID: ${studentId}`

          if (!studentData[studentId]) {
            studentData[studentId] = {
              id: studentId,
              name: studentName,
              Present: 0,
              Absent: 0,
              'Excused Absence': 0,
              Late: 0,
            }
          }

          if (studentData[studentId].hasOwnProperty(record.status)) {
            studentData[studentId][record.status]++
          }
        })

        // Convert to array and sort by total absence count (descending), then by late count (descending)
        const studentArray = Object.values(studentData).sort((a, b) => {
          const totalAbsencesA = a.Absent + a['Excused Absence'];
          const totalAbsencesB = b.Absent + b['Excused Absence'];
          
          // First sort by total absence count (descending)
          if (totalAbsencesB !== totalAbsencesA) {
            return totalAbsencesB - totalAbsencesA;
          }
          // If total absence count is the same, sort by late count (descending)
          if (b.Late !== a.Late) {
            return b.Late - a.Late;
          }
          // If both total absence and late are the same, sort alphabetically by name
          return a.name.localeCompare(b.name);
        })

        setStudentAttendanceData(studentArray)
      } catch (error) {
        console.error('Error in fetchStudentAttendanceData:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchStudentAttendanceData()
  }, [classId, teacherId, viewMode, isAdmin])

  // Chart data for student absence chart
  const chartData = useMemo(() => {
    if (studentAttendanceData.length === 0) return null

    // Show all students, not just top 10
    return {
      labels: studentAttendanceData.map(student => student.name),
      datasets: [
        {
          label: 'Absences (Total)',
          data: studentAttendanceData.map(student => student.Absent + student['Excused Absence']),
          backgroundColor: 'rgba(255, 99, 132, 0.8)',
          borderColor: 'rgb(255, 99, 132)',
          borderWidth: 1,
          // Custom data for tooltips
          customData: studentAttendanceData.map(student => ({
            absent: student.Absent,
            excused: student['Excused Absence'],
            total: student.Absent + student['Excused Absence']
          }))
        },
        {
          label: 'Late',
          data: studentAttendanceData.map(student => student.Late),
          backgroundColor: 'rgba(255, 205, 86, 0.8)',
          borderColor: 'rgb(255, 205, 86)',
          borderWidth: 1,
        },
      ],
    }
  }, [studentAttendanceData])

  // Chart options
  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: 'y', // Horizontal bar chart
    plugins: {
      legend: {
        position: 'top',
        labels: {
          color: theme.palette.text.primary,
          font: {
            family: theme.typography.fontFamily,
            size: 12,
          },
        },
      },
      title: {
        display: true,
        text: `Student Absence Overview (${viewMode.charAt(0).toUpperCase() + viewMode.slice(1)})`,
        color: theme.palette.text.primary,
        font: {
          family: theme.typography.fontFamily,
          size: 16,
          weight: 'bold',
        },
      },
      tooltip: {
        yAlign: 'bottom', // This will position tooltips above the bars
        backgroundColor:
          theme.palette.mode === 'dark'
            ? 'rgba(0, 0, 0, 0.8)'
            : 'rgba(255, 255, 255, 0.8)',
        titleColor: theme.palette.text.primary,
        bodyColor: theme.palette.text.primary,
        borderColor: theme.palette.divider,
        borderWidth: 1,
        callbacks: {
          label: function(context) {
            const dataset = context.dataset;
            const dataIndex = context.dataIndex;
            
            if (dataset.label === 'Absences (Total)' && dataset.customData) {
              const customData = dataset.customData[dataIndex];
              if (customData.total > 0) {
                return [
                  `Total Absences: ${customData.total}`,
                  `  • Excused: ${customData.excused}`
                ];
              }
            }
            
            return `${dataset.label}: ${context.parsed.x}`;
          }
        }
      },
    },
    scales: {
      x: {
        grid: {
          color: theme.palette.divider,
        },
        ticks: {
          color: theme.palette.text.secondary,
        },
      },
      y: {
        grid: {
          color: theme.palette.divider,
        },
        ticks: {
          color: theme.palette.text.secondary,
        },
      },
    },
  }), [viewMode, theme])

  // Chart container style - increase height to accommodate more students
  const chartContainerStyle = {
    height: Math.max(400, studentAttendanceData.length * 30), // Dynamic height based on number of students
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <TimePeriodFilter value={viewMode} onChange={handleViewModeChange} />
        <Box sx={chartContainerStyle}>
          <Typography variant="body1" color="text.secondary">
            Loading student attendance data...
          </Typography>
        </Box>
      </Box>
    )
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <TimePeriodFilter value={viewMode} onChange={handleViewModeChange} />

      {studentAttendanceData.length === 0 ? (
        <Box sx={chartContainerStyle}>
          <Typography variant="body1" color="text.secondary">
            No attendance data available for the selected period.
          </Typography>
        </Box>
      ) : (
        <>

          <Box sx={chartContainerStyle}>
            <Bar options={chartOptions} data={chartData} />
          </Box>

          {/* Summary statistics */}
          <Box sx={{ 
            mt: 'auto',
            p: 2,
            backgroundColor: theme.palette.background.default,
            borderRadius: theme.shape.borderRadius
          }}>
            <Typography variant="subtitle2" gutterBottom>
              About this chart:
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Showing all {studentAttendanceData.length} students in the selected class.
            </Typography>
            {studentAttendanceData.length > 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Students are sorted by total absence count (highest to lowest). Hover over bars to see breakdown of unexcused vs excused absences.
              </Typography>
            )}
          </Box>
        </>
      )}
    </Box>
  )
}

export default React.memo(ClassStudentAttendanceChart) 